import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import StripeSettings from './StripeSettings';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import {
  PlatformSettings,
  getSettings,
  updateSettings,
} from '../api/settings';
import { getProfile, updateProfile } from '../api/auth';
import { Pricing, getPricing, updatePricing } from '../api/pricing';
import { extractApiError } from '../api/legalServices';

const INITIAL: PlatformSettings = {
  supportEmail: 'support@rentagade.com',
};

// Mirrors services/applicationFees.js on the backend.
const MIN_FEE = 1;
const MAX_FEE = 10000;

// Mirrors the User model's password length check.
const MIN_PASSWORD = 6;

const DISABLED_BTN: React.CSSProperties = { opacity: 0.5, cursor: 'not-allowed' };

interface CardProps {
  icon: string;
  title: string;
  delay: number;
  children: React.ReactNode;
}

const Card: React.FC<CardProps> = ({ icon, title, delay, children }) => (
  <div className="card p-6 animate-fadeIn" style={{ animationDelay: `${delay}ms` }}>
    <div className="flex items-center gap-2.5 mb-5">
      <Icon name={icon} size={20} color="var(--brand)" />
      <span className="text-base font-bold">{title}</span>
    </div>
    {children}
  </div>
);

interface FieldProps {
  label: string;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, hint, error, children }) => (
  <div className="mb-4">
    <label className="form-label">{label}</label>
    {children}
    {(error || hint) && (
      <div className="text-[12px] mt-1.5" style={{ color: error ? 'var(--red)' : 'var(--g500)' }}>
        {error || hint}
      </div>
    )}
  </div>
);

const initials = (name: string): string =>
  name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

// ─── Admin profile ─────────────────────────────────────────────────

const ProfileCard: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState<string>(user?.email ?? '');
  const [savedName, setSavedName] = useState<string>(user?.name ?? '');
  const [name, setName] = useState<string>(user?.name ?? '');
  const [saving, setSaving] = useState<boolean>(false);

  // The session copy can be stale, so refresh it from the server.
  useEffect(() => {
    let cancelled = false;
    getProfile()
      .then((p) => {
        if (cancelled) return;
        setEmail(p.email);
        setSavedName(p.name || '');
        setName(p.name || '');
        updateUser({ id: p.id, name: p.name, email: p.email, role: p.role });
      })
      .catch(() => {
        /* keep showing the session copy */
      });
    return () => {
      cancelled = true;
    };
  }, [updateUser]);

  const trimmed = name.trim();
  const canSave = trimmed !== '' && trimmed !== savedName && !saving;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const p = await updateProfile({ name: trimmed });
      setSavedName(p.name);
      setName(p.name);
      // Keeps the sidebar name in sync.
      updateUser({ id: p.id, name: p.name, email: p.email, role: p.role });
      toast('Profile updated', 'success');
    } catch (err) {
      toast(extractApiError(err, 'Failed to update profile'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card icon="user" title="Admin Profile" delay={0}>
      <div className="flex items-center gap-4 mb-5">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-extrabold flex-shrink-0"
          style={{ background: 'var(--brand-bg)', color: 'var(--brand)' }}
        >
          {initials(savedName || 'A')}
        </div>
        <div className="min-w-0">
          <div className="text-base font-bold text-ink truncate">{savedName || 'Admin'}</div>
          <div className="text-[13px] text-g500 truncate">{email}</div>
        </div>
        <span
          className="ml-auto inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider flex-shrink-0"
          style={{ background: 'var(--brand-bg)', color: 'var(--brand)' }}
        >
          Admin
        </span>
      </div>

      <form onSubmit={save}>
        <Field label="Full Name" error={trimmed === '' ? 'Name is required.' : null}>
          <input
            className="form-input"
            value={name}
            maxLength={100}
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Email" hint="Used to sign in. It can't be changed here.">
          <input
            type="email"
            className="form-input"
            value={email}
            disabled
            style={{ background: 'var(--g50)', color: 'var(--g500)', cursor: 'not-allowed' }}
          />
        </Field>
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canSave}
            style={canSave ? undefined : DISABLED_BTN}
          >
            {saving ? 'Saving...' : 'Save profile'}
          </button>
        </div>
      </form>
    </Card>
  );
};

// ─── Change password ───────────────────────────────────────────────

interface PasswordInputProps {
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  invalid?: boolean;
}

const PasswordInput: React.FC<PasswordInputProps> = ({ value, onChange, autoComplete, invalid }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        className="form-input pr-11"
        style={invalid ? { borderColor: 'var(--red)' } : undefined}
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-g100"
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        <Icon name={show ? 'eye-off' : 'eye'} size={16} color="var(--g500)" />
      </button>
    </div>
  );
};

const EMPTY_PW = { current: '', next: '', confirm: '' };

const PasswordCard: React.FC = () => {
  const { toast } = useToast();
  const [pw, setPw] = useState(EMPTY_PW);
  const [saving, setSaving] = useState<boolean>(false);
  // Bumped after a successful change to reset the show/hide toggles.
  const [formKey, setFormKey] = useState<number>(0);

  const nextError =
    pw.next === ''
      ? null
      : pw.next.length < MIN_PASSWORD
        ? `Must be at least ${MIN_PASSWORD} characters.`
        : pw.next === pw.current
          ? 'Must be different from your current password.'
          : null;
  const confirmError = pw.confirm !== '' && pw.confirm !== pw.next ? 'Passwords do not match.' : null;
  const canSave =
    pw.current !== '' && pw.next !== '' && pw.confirm !== '' && !nextError && !confirmError && !saving;

  const set = (k: keyof typeof EMPTY_PW) => (v: string) => setPw((p) => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      await updateProfile({
        currentPassword: pw.current,
        newPassword: pw.next,
        confirmNewPassword: pw.confirm,
      });
      setPw(EMPTY_PW);
      setFormKey((k) => k + 1);
      toast('Password updated', 'success');
    } catch (err) {
      toast(extractApiError(err, 'Failed to update password'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card icon="lock" title="Change Password" delay={80}>
      <form key={formKey} onSubmit={submit}>
        <Field label="Current Password">
          <PasswordInput value={pw.current} onChange={set('current')} autoComplete="current-password" />
        </Field>
        <Field label="New Password" hint={`At least ${MIN_PASSWORD} characters.`} error={nextError}>
          <PasswordInput
            value={pw.next}
            onChange={set('next')}
            autoComplete="new-password"
            invalid={Boolean(nextError)}
          />
        </Field>
        <Field label="Confirm New Password" error={confirmError}>
          <PasswordInput
            value={pw.confirm}
            onChange={set('confirm')}
            autoComplete="new-password"
            invalid={Boolean(confirmError)}
          />
        </Field>
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canSave}
            style={canSave ? undefined : DISABLED_BTN}
          >
            <Icon name="lock" size={14} color="#fff" />
            {saving ? 'Updating...' : 'Update password'}
          </button>
        </div>
      </form>
    </Card>
  );
};

// ─── Platform ──────────────────────────────────────────────────────

const PlatformCard: React.FC = () => {
  const { toast } = useToast();
  const [state, setState] = useState<PlatformSettings>(INITIAL);
  const [original, setOriginal] = useState<PlatformSettings>(INITIAL);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const fetched = await getSettings();
      // Merge so any keys missing from the backend stay at their defaults.
      const merged = { ...INITIAL, ...fetched };
      setState(merged);
      setOriginal(merged);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = state.supportEmail !== original.supportEmail;
  const canSave = dirty && !saving;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const updated = await updateSettings({ supportEmail: state.supportEmail });
      const merged = { ...INITIAL, ...updated };
      setState(merged);
      setOriginal(merged);
      toast('Settings saved', 'success');
    } catch (err) {
      toast(extractApiError(err, 'Failed to save settings'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card icon="building-2" title="Support Email" delay={160}>
      {loading ? (
        <div className="text-[13px] text-g500">Loading settings...</div>
      ) : loadError ? (
        <div className="text-[13px]" style={{ color: 'var(--red)' }}>
          <div className="mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      ) : (
        <form onSubmit={handleSave}>
          <Field label="Email">
            <input
              type="email"
              className="form-input"
              value={state.supportEmail}
              onChange={(e) => setState((s) => ({ ...s, supportEmail: e.target.value }))}
            />
          </Field>
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!canSave}
              style={canSave ? undefined : DISABLED_BTN}
            >
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      )}
    </Card>
  );
};

// ─── Application fees ──────────────────────────────────────────────

type FeeKey = keyof Pricing['applicationFees'];

const FEE_FIELDS: { key: FeeKey; label: string; hint: string }[] = [
  {
    key: 'rental',
    label: 'Rental listings',
    hint: 'Charged when a renter applies for a rental property.',
  },
  {
    key: 'sale',
    label: 'Buy listings',
    hint: 'Charged when a buyer applies for a property for sale.',
  },
];

const feeError = (v: string): string | null => {
  if (v.trim() === '') return 'Enter an amount.';
  const n = Number(v);
  if (!Number.isFinite(n)) return 'Enter a valid amount.';
  if (n < MIN_FEE) return `Must be at least $${MIN_FEE}.`;
  if (n > MAX_FEE) return `Can't be more than $${MAX_FEE.toLocaleString()}.`;
  return null;
};

const ApplicationFeesCard: React.FC = () => {
  const { toast } = useToast();
  const [fees, setFees] = useState<Record<FeeKey, string>>({ rental: '', sale: '' });
  const [original, setOriginal] = useState(fees);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const apply = useCallback((p: Pricing) => {
    const next = {
      rental: String(p.applicationFees.rental),
      sale: String(p.applicationFees.sale),
    };
    setFees(next);
    setOriginal(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      apply(await getPricing());
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load application fees'));
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    load();
  }, [load]);

  const errors = {
    rental: feeError(fees.rental),
    sale: feeError(fees.sale),
  };
  const changed = FEE_FIELDS.filter((f) => Number(fees[f.key]) !== Number(original[f.key]));
  const canSave =
    changed.length > 0 && !errors.rental && !errors.sale && !saving;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    const diff: Partial<Pricing['applicationFees']> = {};
    changed.forEach((f) => {
      diff[f.key] = Number(fees[f.key]);
    });
    setSaving(true);
    try {
      apply(await updatePricing(diff));
      toast('Application fees updated', 'success');
    } catch (err) {
      toast(extractApiError(err, 'Failed to save application fees'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card icon="dollar-sign" title="Application Fees" delay={0}>
      <div className="text-[13px] text-g500 mb-4 -mt-2 leading-relaxed">
        The one-time fee charged when someone submits an application in the app.
        It depends on the listing's type. New amounts apply to new applications
        right away; applications already paid aren't affected.
      </div>
      {loading ? (
        <div className="text-[13px] text-g500">Loading fees...</div>
      ) : loadError ? (
        <div className="text-[13px]" style={{ color: 'var(--red)' }}>
          <div className="mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      ) : (
        <form onSubmit={save}>
          <div className="grid gap-x-4 sm:grid-cols-2">
            {FEE_FIELDS.map((f) => (
              <Field key={f.key} label={f.label} hint={f.hint} error={errors[f.key]}>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-g500">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={MIN_FEE}
                    max={MAX_FEE}
                    step="0.01"
                    className="form-input pl-7"
                    style={errors[f.key] ? { borderColor: 'var(--red)' } : undefined}
                    value={fees[f.key]}
                    onChange={(e) => setFees((s) => ({ ...s, [f.key]: e.target.value }))}
                  />
                </div>
              </Field>
            ))}
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!canSave}
              style={canSave ? undefined : DISABLED_BTN}
            >
              {saving ? 'Saving...' : 'Save fees'}
            </button>
          </div>
        </form>
      )}
    </Card>
  );
};

const GeneralSettings: React.FC = () => (
  <div className="grid gap-5 lg:grid-cols-2 items-start" style={{ maxWidth: 1100 }}>
    <div className="flex flex-col gap-5">
      <ProfileCard />
      <PlatformCard />
    </div>
    <PasswordCard />
  </div>
);

// Prices the platform charges, managed in one place.
const PricingSettings: React.FC = () => (
  <div className="grid gap-5 lg:grid-cols-2 items-start" style={{ maxWidth: 1100 }}>
    <ApplicationFeesCard />
  </div>
);

type Tab = 'general' | 'stripe' | 'pricing';

const TAB_IDS: Tab[] = ['general', 'stripe', 'pricing'];

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'general', label: 'General', icon: 'settings' },
  { id: 'stripe', label: 'Stripe Payments', icon: 'key' },
  { id: 'pricing', label: 'Pricing Configuration', icon: 'dollar-sign' },
];

const Settings: React.FC = () => {
  // The tab lives in the URL so links like /admin/settings?tab=stripe open it directly.
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') as Tab | null;
  const tab: Tab = requested && TAB_IDS.includes(requested) ? requested : 'general';

  return (
    <div>
      <div className="flex mb-5 overflow-x-auto" style={{ borderBottom: '2px solid var(--g100)' }} role="tablist">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              onClick={() => setParams(t.id === 'general' ? {} : { tab: t.id }, { replace: true })}
              className="flex items-center gap-2 px-5 py-3 text-sm font-semibold whitespace-nowrap transition-all"
              style={{
                color: active ? 'var(--brand)' : 'var(--g500)',
                marginBottom: -2,
                background: 'none',
                border: 'none',
                borderBottomWidth: 2,
                borderBottomStyle: 'solid',
                borderBottomColor: active ? 'var(--brand)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <Icon name={t.icon} size={16} color={active ? 'var(--brand)' : 'var(--g500)'} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Both panels stay mounted so unsaved edits survive a tab switch. */}
      <div role="tabpanel" hidden={tab !== 'general'}>
        <GeneralSettings />
      </div>
      <div role="tabpanel" hidden={tab !== 'stripe'}>
        <StripeSettings />
      </div>
      <div role="tabpanel" hidden={tab !== 'pricing'}>
        <PricingSettings />
      </div>
    </div>
  );
};

export default Settings;
